import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { ChangeEvent, FC, forwardRef } from 'react';

import { CheckMarkIcon } from '@gem/icons';
import {
  IconButton,
  IconButtonShape,
  IconViewSizes,
  Popover,
  TextField,
  FieldProps,
  customScrollStyles,
} from '@gem/ui-kit';

import { User } from 'APP/store/Users/User/User';

import { useAutocomletePresenter } from './Autocomplete.presenter';
import styles from './Autocomplete.styles.m.css';
import { MemberItem } from './MemberItem';

interface IAutocompleteProps extends Omit<FieldProps, 'value' | 'onChange'> {
  value: Array<string>;
  onChange(value: Array<string>): void;
  removeParticipant(email: string): void;
  setParticipant(user: User): void;
  classNameContainer: string;
}

export const Autocomplete: FC<IAutocompleteProps> = observer(
  forwardRef((props, ref) => {
    const {
      value,
      hasError,
      message,
      label,
      classNameContainer,
      onChange,
      removeParticipant,
      setParticipant,
      ...rest
    } = props;

    const { presenter, anchorRef } = useAutocomletePresenter({
      value,
      onChange,
      removeParticipant,
      setParticipant,
    });

    const onChangeSearch = (event: ChangeEvent<HTMLInputElement>): void => {
      presenter.onSearch(event.target.value);
    };

    const forceAddParticipant = (): void => {
      presenter.forceAdd(presenter.searchText);
    };

    return (
      <>
        <div className={classNames(styles.container, classNameContainer)}>
          <TextField
            {...rest}
            ref={(element): void => {
              if (ref instanceof Function) ref(element);
              anchorRef.current = element;
            }}
            label={label}
            onChange={onChangeSearch}
            value={presenter.searchText}
            hasError={hasError}
            message={message || ' '}
            className={styles.textField}
            onFocus={presenter.onFocusParticipants}
          />
          <IconButton
            isDisabled={!presenter.canSelectMore || presenter.isUserFromList}
            onClick={forceAddParticipant}
            Icon={CheckMarkIcon}
            theme="20"
            size={IconViewSizes.MLarge}
            iconSize={IconViewSizes.Medium}
            shape={IconButtonShape.Square}
          />
        </div>
        <Popover
          isOpened={presenter.isOpenPopover}
          onClose={presenter.onClosePopover}
          reference={anchorRef.current}
          placement="bottom-end"
          className={styles.popover}
          isFullWidth
        >
          <div className={classNames(styles.containerList, customScrollStyles.container)}>
            {presenter.members.map((member) => {
              return (
                <MemberItem
                  key={member.id}
                  member={member}
                  toggleMember={presenter.toggleContact}
                  getIsSelected={presenter.getIsSelected}
                />
              );
            })}
          </div>
        </Popover>
      </>
    );
  })
);
